import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useEffect, useState} from 'react';
import {FOODIE_MEAL_TIME_MODEL} from '../Model/Models';
import HandleChange from '../../../../../Helpers/HandleChange';
import {CreateMealTime, UpdateMealTime} from '../../../../../reduxToolkit/FoodieMealTime/MealTimeApi';
import {toast} from 'react-toastify';
import {errorMsg, isEndTimeAfterStartTime, validateDecimal} from "../../../../../Apis/functions";

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * @param show - Boolean to determine if the delete schedule modal should be shown
 * @param onHide - Function to hide the modal when triggered
 * @param {Function} setModified - Setter used to update the `modified` flag and trigger data reloads (required).
 * @constructor
 */
const AddEditMealApp = ({ show, onHide, setModified }) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { mealById } = useSelector(state => state.MealTimeSlice);
    const costInfo = useSelector((state) => state.employeeFoodieSlice)?.getFirstFoodieSetting;
    const [disabled, setDisabled] = useState(false);
    const [emptyFields, setEmptyFields] = useState(false);
    const [item, setItem] = useState({...FOODIE_MEAL_TIME_MODEL});
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [isValidTime, setIsValidTime] = useState(true);
    const [isValidCost, setIsValidCost] = useState(true);
    const [isValidCostText, setIsValidCostText] = useState('this_value_must_be_greater_than_0');
    const showCostInfo =  costInfo?.isBaseCost === false && costInfo?.isMealTimeCost === true;

    /**
     * Function to set values to the meal model.
     */
    useEffect(() => {
        if(show && mealById?.id){
            setItem({...FOODIE_MEAL_TIME_MODEL, ...mealById})
        }
    }, [show,mealById]);

    /**
     * useEffect to validate dates.
     */
    useEffect(() => {
        if(item?.endTime && item?.startTime){
            setIsValidTime(isEndTimeAfterStartTime(item?.startTime, item?.endTime))
        }
    }, [item?.endTime, item?.startTime]);

    /**
     * Function to create or update a meal.
     */
    const handleSubmit = () => {
        if(!item.isValid(showCostInfo) || !isValidTime || !isValidCost){
            setEmptyFields(true);
            return;
        }
        let body = item.toAddEdit();

        dispatch(item?.id ? UpdateMealTime(body) : CreateMealTime(body) ).then(({ payload: { data: { data, code } } }) => {
            if (data?.id) {
                setModified(true);
                toast.success(t(item?.id ? 'has_been_updated_successfully' : 'has_been_created_successfully'));
                onHide();
            } else  errorMsg(code);
        });
    };


    const handleClearFields = () => {
        setItem({...FOODIE_MEAL_TIME_MODEL});
        setEmptyFields(false);
    }

    /**
     * Validates the meal time cost input.
     * @author Karen Yunuen Pozos Arias
     * This function only runs validation when the meal time cost option is enabled.
     * It checks if the entered value is a valid number greater than zero.
     * If the input is empty, it resets the validation state to valid.
     *
     * @param {Event} e - Input change event containing the cost value.
     */
    const validateMealTimeCost = (e) => {
        if (!costInfo?.isMealTimeCost) return;
        const rawValue = e.target.value;
        if (rawValue === '') {
            setIsValidCost(true);
            return;
        }
        const { isValid, text } = validateDecimal(rawValue);
        setIsValidCostText(text);
        setIsValidCost(isValid);
    };

    return{
        t,
        disabled, setDisabled,
        emptyFields, setEmptyFields,
        item, setItem,
        handleChange,
        isValidTime,
        handleSubmit,
        handleClearFields,
        mealById,
        costInfo,
        isValidCost,
        isValidCostText,
        validateMealTimeCost
    }

}

export  default AddEditMealApp;
